// Fit Assessment section - JD analyzer

import { useState } from 'react';
import {
  useJDAnalyzer,
  STRONG_FIT_EXAMPLE,
  WEAK_FIT_EXAMPLE,
} from '../../hooks/useJDAnalyzer';
import { FitVerdict } from '../../lib/api';

export function FitAssessment() {
  const [jobDescription, setJobDescription] = useState('');
  const [activeExample, setActiveExample] = useState<'strong' | 'weak' | null>(
    null
  );
  const { result, isLoading, error, analyze, clearResult } = useJDAnalyzer();

  const handleExampleToggle = (example: 'strong' | 'weak') => {
    if (activeExample === example) {
      setActiveExample(null);
      setJobDescription('');
      clearResult();
    } else {
      setActiveExample(example);
      setJobDescription(
        example === 'strong' ? STRONG_FIT_EXAMPLE : WEAK_FIT_EXAMPLE
      );
      clearResult();
    }
  };

  const handleAnalyze = () => {
    if (jobDescription.trim()) {
      analyze(jobDescription);
    }
  };

  return (
    <section id="fit-check" className="section bg-white">
      <div className="container-custom">
        {/* Section header */}
        <h2 className="section-title">Honest Fit Assessment</h2>
        <p className="section-subtitle">
          Paste a job description. Get an honest assessment of whether I'm the
          right person—including when I'm not.
        </p>

        <div className="max-w-3xl">
          {/* Example toggles */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => handleExampleToggle('strong')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeExample === 'strong'
                  ? 'bg-success/10 text-success border border-success/30'
                  : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
              }`}
            >
              Strong Fit Example
            </button>
            <button
              onClick={() => handleExampleToggle('weak')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeExample === 'weak'
                  ? 'bg-warning/10 text-warning border border-warning/30'
                  : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
              }`}
            >
              Weak Fit Example
            </button>
          </div>

          {/* Job description input */}
          <textarea
            value={jobDescription}
            onChange={(e) => {
              setJobDescription(e.target.value);
              setActiveExample(null);
            }}
            placeholder="Paste job description here..."
            className="textarea h-48 mb-4"
          />

          {/* Analyze button */}
          <button
            onClick={handleAnalyze}
            disabled={!jobDescription.trim() || isLoading}
            className="btn-primary w-full sm:w-auto mb-8 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Analyzing...
              </span>
            ) : (
              'Analyze Fit'
            )}
          </button>

          {/* Error display */}
          {error && (
            <div className="p-4 bg-error/10 border border-error/30 rounded-lg text-error mb-6">
              {error}
            </div>
          )}

          {/* Results */}
          {result && <FitResultPanel result={result} />}

          {/* Philosophy callout */}
          <div className="mt-8 p-4 bg-accent/5 border border-accent/20 rounded-lg">
            <p className="text-sm text-text-secondary italic">
              "Let's collaborate. This tool saves us both time by being upfront
              about fit."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Result panel component
interface FitResultPanelProps {
  result: {
    verdict: FitVerdict;
    headline: string;
    opening: string;
    gaps: Array<{
      requirement: string;
      gap_title: string;
      explanation: string;
    }>;
    transfers: string;
    recommendation: string;
  };
}

function FitResultPanel({ result }: FitResultPanelProps) {
  const verdictConfig = {
    strong_fit: {
      label: 'Strong Fit',
      className: 'verdict-strong',
      icon: '✓',
    },
    worth_conversation: {
      label: 'Worth a Conversation',
      className: 'verdict-worth',
      icon: '○',
    },
    probably_not: {
      label: 'Probably Not Your Person',
      className: 'verdict-not',
      icon: '⚠️',
    },
  };

  const config = verdictConfig[result.verdict];

  return (
    <div className="card border-2 animate-fade-in">
      {/* Verdict header */}
      <div
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border mb-4 ${config.className}`}
      >
        <span>{config.icon}</span>
        <span className="font-semibold">Honest Assessment — {config.label}</span>
      </div>

      {/* Headline */}
      <h3 className="font-serif text-xl font-semibold text-text-primary mb-2">
        {result.headline}
      </h3>

      {/* Opening */}
      <p className="text-text-secondary mb-6">{result.opening}</p>

      {/* Gaps */}
      {result.gaps.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-text-primary uppercase text-sm tracking-wide mb-3">
            Where I Don't Fit
          </h4>
          <div className="space-y-3">
            {result.gaps.map((gap, index) => (
              <div
                key={index}
                className="flex gap-3 p-3 bg-warning/5 rounded-lg border border-warning/20"
              >
                <span className="text-warning mt-0.5">✗</span>
                <div>
                  <p className="font-medium text-text-primary">
                    {gap.gap_title}
                  </p>
                  <p className="text-sm text-text-secondary mt-1">
                    {gap.explanation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transfers */}
      {result.transfers && (
        <div className="mb-6">
          <h4 className="font-semibold text-text-primary uppercase text-sm tracking-wide mb-3">
            What Transfers
          </h4>
          <p className="text-text-secondary">{result.transfers}</p>
        </div>
      )}

      {/* Recommendation */}
      <div className="p-4 bg-gray-50 rounded-lg border border-border/30">
        <h4 className="font-semibold text-text-primary uppercase text-sm tracking-wide mb-2">
          My Recommendation
        </h4>
        <p className="text-text-secondary">{result.recommendation}</p>
      </div>
    </div>
  );
}
